import React from "react";
import { Provider } from "react-redux";
import { createRoot } from "react-dom/client";

// Self-hosted so no visitor request goes to Google Fonts.
import "@fontsource/open-sans/400.css";
import "@fontsource/open-sans/500.css";
import "@fontsource/open-sans/600.css";
import "@fontsource/open-sans/700.css";
// Import shared UI styles (Tailwind CSS + Lemonade brand variables)
import "@lemonadetraining/web-ui/styles/globals.css";
import "./index.css";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import { AuthContextProvider, firebaseAuth } from "@lemonadetraining/web-shared";
import { appReduxStore } from "@lemonadetraining/web-shared";
import { ThemeProvider } from "@lemonadetraining/web-ui";

const container = document.getElementById("root");
if (!container) {
  throw new Error("Root element #root was not found in the document; the app cannot mount.");
}
const root = createRoot(container);

async function bootstrap() {
  // Keep this condition inline and the imports dynamic: CRA folds it to false in
  // every production build and drops the dev sign-in module from the bundle.
  if (process.env.NODE_ENV !== "production") {
    try {
      const { signInAsEmulatorUser } = await import("./devtools/emulatorSignIn");
      await signInAsEmulatorUser({
        // Without the flag the module only refuses, so a request is never silently ignored.
        enabled: process.env.REACT_APP_USE_EMULATOR === "true",
        auth: firebaseAuth,
        location: window.location,
        history: window.history,
      });
    } catch (error) {
      console.error("[emulator sign-in] refused:", error);
      try {
        const { default: EmulatorSignInErrorScreen } =
          await import("./devtools/EmulatorSignInErrorScreen");
        root.render(
          <ThemeProvider defaultTheme="light" storageKey="lemonade-app-theme">
            <EmulatorSignInErrorScreen error={error} />
          </ThemeProvider>
        );
      } catch {
        root.render(<p role="alert">Emulator sign-in failed: {String(error)}</p>);
      }
      return;
    }
  }

  root.render(
    <React.StrictMode>
      <Provider store={appReduxStore}>
        <AuthContextProvider>
          <ThemeProvider defaultTheme="light" storageKey="lemonade-app-theme">
            <App />
          </ThemeProvider>
        </AuthContextProvider>
      </Provider>
    </React.StrictMode>
  );
}

bootstrap();

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
